import { isRef, onBeforeUnmount, ref, unref } from 'vue'
import { createClientRequestId, getReportOptimisticVersion } from '../workReportUtils'
import { createWorkReportDraftStateMachine } from './workReportDraftStateMachine.mjs'
import { createConflictRecoverySave, isVersionConflict } from './workReportDraftRecovery.mjs'

const DEFAULT_MAX_CONFLICT_RETRIES = 2

function refreshReport(report, savedReport) {
  if (!savedReport || typeof savedReport !== 'object' || Array.isArray(savedReport)) {
    return
  }

  if (isRef(report)) {
    if (report.value && typeof report.value === 'object') {
      Object.assign(report.value, savedReport)
    } else {
      report.value = savedReport
    }
    return
  }

  if (report && typeof report === 'object') {
    Object.assign(report, savedReport)
  }
}

/**
 * Manage debounced work-report draft saves with optimistic locking.
 * The save callback receives a payload containing expectedVersion and clientRequestId.
 *
 * When a save fails with a version conflict, the composable self-heals: it calls `resync`
 * to pull the latest optimistic version into `report`, then retries the same user edits with
 * a fresh version and idempotency key (up to `maxConflictRetries`). Only when recovery is
 * impossible (report status advanced, retries exhausted) does the conflict reach `onConflict`.
 *
 * @param {Object} options - Draft state options.
 * @param {Object|import('vue').Ref<Object>} options.report - Current work report.
 * @param {(payload: Object) => Promise<Object>} options.save - Save callback.
 * @param {(error: Error) => void|Promise<void>} [options.onConflict] - Conflict callback (unrecoverable).
 * @param {(result: Object) => void|Promise<void>} [options.onSaved] - Save success callback.
 * @param {() => Promise<boolean>} [options.resync] - Refresh the latest optimistic version into `report`;
 *   resolves true when it is safe to retry, false when the user must reload.
 * @param {number} [options.maxConflictRetries=2] - Max automatic conflict-recovery retries per save.
 * @returns {Object} Draft state and controls.
 */
export function useWorkReportDraft({
  report,
  save,
  onConflict,
  onSaved,
  resync,
  maxConflictRetries = DEFAULT_MAX_CONFLICT_RETRIES
} = {}) {
  const hasUnsavedChanges = ref(false)
  const saveStatus = ref('idle')

  // 冲突自愈：版本冲突时先重同步最新版本，再以新的版本号与幂等键重试（保留用户当前编辑）；
  // 若无法自愈（状态已前进或超过重试上限）则抛出冲突，由状态机走 onConflict 提示重新加载。
  const saveWithRecovery = createConflictRecoverySave({
    save,
    resync,
    getExpectedVersion: () => getReportOptimisticVersion(unref(report)),
    createClientRequestId,
    maxConflictRetries
  })

  const draftState = createWorkReportDraftStateMachine({
    getExpectedVersion: () => getReportOptimisticVersion(unref(report)),
    createClientRequestId,
    save: saveWithRecovery,
    isVersionConflict,
    onConflict,
    onSaved: async (savedReport) => {
      refreshReport(report, savedReport)

      if (typeof onSaved === 'function') {
        await onSaved(savedReport)
      }
    },
    onStateChange: (state) => {
      hasUnsavedChanges.value = state.hasUnsavedChanges
      if (state.saveStatus === 'conflict') {
        saveStatus.value = 'conflict'
        return
      }
      saveStatus.value = state.saveStatus
    }
  })

  onBeforeUnmount(() => {
    draftState.dispose()
  })

  return {
    markDirty: draftState.markDirty,
    flush: draftState.flush,
    retry: draftState.retry,
    cancelAutoSave: draftState.cancelAutoSave,
    discard: draftState.discard,
    dispose: draftState.dispose,
    hasUnsavedChanges,
    saveStatus
  }
}
